<script lang="ts">
  import { onMount, tick, untrack } from 'svelte';
  import { focusFirst } from '../lib/input/spatial.ts';
  import { engine } from '../lib/player/engine.svelte.ts';
  import {
    api, type InfoEvent, type InfoLineupPlayer, type InfoMatch, type InfoPlayer, type InfoTableRow,
  } from '../lib/api/client.ts';

  interface Props {
    /** Whether the remote's focus is in the panel; only then are its controls reachable. */
    focused: boolean;
    onClose: () => void;
  }
  const { focused, onClose }: Props = $props();

  type Tab = 'spiel' | 'tabelle' | 'aufstellung';
  let tab = $state<Tab>('spiel');

  /**
   * The match shown is the one on the main picture, so switching angles in a
   * conference follows along. Other angles are the fallback when the main
   * angle is not linked to a match.
   */
  const linked = $derived(
    engine.angles.find((a) => a.id === engine.mainAngleId && a.fixtureExternalId)
      ?? engine.angles.find((a) => a.fixtureExternalId)
      ?? null,
  );
  const matchId = $derived(linked?.fixtureProvider === 'fotmob' ? linked.fixtureExternalId : null);

  /**
   * How far the picture of that match runs behind real time. Everything is
   * requested as of that moment -- a live table or a goal in the event list
   * would otherwise give away what the picture shows only half a minute later.
   */
  const behindSeconds = $derived.by(() => {
    if (!linked) return null;
    const angle = linked.role === 'audio'
      ? engine.angles.find((a) => a.fixtureExternalId === linked.fixtureExternalId && a.role !== 'audio') ?? linked
      : linked;
    const behind = engine.statsFor(angle.id)?.behindRealTime ?? null;
    return behind === null ? null : behind + angle.syncOffsetMs / 1000;
  });

  let match = $state<InfoMatch | null>(null);
  let matchAhead = $state(false);
  /** Portraits that failed to load (FotMob answers 403 where there is none). */
  let noPhoto = $state(new Set<string>());
  const hidePhoto = (url: string) => { noPhoto = new Set(noPhoto).add(url); };
  let tables = $state<Array<{ name: string; rows: InfoTableRow[] }> | null>(null);
  let tableAhead = $state(false);
  let player = $state<InfoPlayer | null>(null);
  let playerLoading = $state(false);
  let error = $state<string | null>(null);
  let now = $state(Date.now());

  const pictureTime = () => Date.now() - Math.max(0, behindSeconds ?? 0) * 1000;

  async function refresh() {
    if (!matchId) return;
    try {
      const m = await api.infoMatch(matchId, pictureTime());
      // A panel switched to another match meanwhile must not show this answer.
      if (m.data.id !== matchId) return;
      match = m.data;
      matchAhead = m.ahead;
      error = null;
      if (tab === 'tabelle' && m.data.leagueId) {
        const t = await api.infoTable(m.data.leagueId, pictureTime());
        tables = t.data;
        tableAhead = t.ahead;
      }
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    }
  }

  // A new match or tab loads at once instead of waiting for the next tick.
  $effect(() => {
    const _deps = [matchId, tab];
    void _deps;
    // Only those two: the picture delay changes every second and must not
    // trigger a reload, nor close an open player profile.
    untrack(() => {
      player = null;
      void refresh();
    });
  });

  onMount(() => {
    const poll = setInterval(() => void refresh(), 15_000);
    const clock = setInterval(() => { now = Date.now(); }, 5_000);
    return () => { clearInterval(poll); clearInterval(clock); };
  });

  /** Match clock as of the picture, not as of real time. */
  const clockLabel = $derived.by(() => {
    if (!match) return '';
    const t = now - Math.max(0, behindSeconds ?? 0) * 1000;
    const min = (from: number | null, base: number, cap: number) => {
      if (from === null) return '';
      const m = base + Math.floor((t - from) / 60_000) + 1;
      return m > cap ? `${cap}+` : `${Math.max(base + 1, m)}'`;
    };
    switch (match.phase) {
      case 1: return match.kickoff
        ? new Date(match.kickoff).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
        : 'vor Anpfiff';
      case 2: return min(match.firstHalfStart, 0, 45);
      case 3: return min(match.secondHalfStart, 45, 90);
      case 10: return 'Halbzeit';
      case 6: return 'Ende';
      default: return 'läuft';
    }
  });

  const ICON: Record<InfoEvent['kind'], string> = {
    goal: '⚽', penalty: '⚽', owngoal: '⚽', yellow: '🟨', yellowred: '🟨🟥', red: '🟥', penaltyMissed: '❌', penaltyAwarded: '❗',
  };
  const NOTE: Partial<Record<InfoEvent['kind'], string>> = {
    penalty: ' (Elfmeter)', owngoal: ' (Eigentor)', penaltyMissed: ' (Elfmeter verschossen)', penaltyAwarded: 'Elfmeter (nach VAR)',
  };

  async function openPlayer(p: InfoLineupPlayer | { id: string | null }) {
    if (!p.id) return;
    playerLoading = true;
    try {
      player = await api.infoPlayer(p.id);
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      playerLoading = false;
    }
    await refocus();
  }

  /**
   * The button that was pressed disappears with the view it belonged to; on a
   * remote focus would then sit on <body> and the arrows would do nothing.
   */
  async function refocus() {
    await tick();
    if (!focused || !panelEl) return;
    focusFirst(panelEl);
  }

  async function closePlayer() {
    player = null;
    await refocus();
  }

  let panelEl = $state<HTMLElement | null>(null);

  /** Before kickoff and after the whistle there is nothing to give away. */
  const running = $derived(match !== null && match.phase !== 1 && match.phase !== 6);

  const euro = (v: number | null) => {
    if (v === null) return null;
    return v >= 1_000_000
      ? `${(v / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Mio. €`
      : `${Math.round(v / 1000).toLocaleString('de-DE')} Tsd. €`;
  };

  /** FotMob only sends English titles for the percentile comparison. */
  const TRAITS: Record<string, string> = {
    'Chances created': 'Chancen kreiert', 'Aerial duels': 'Kopfballduelle', 'Aerial duels won': 'Kopfballduelle',
    'Defensive actions': 'Defensivaktionen', 'Goals': 'Tore', 'Shot attempts': 'Torschüsse',
    'Touches': 'Ballkontakte', 'Saves': 'Paraden', 'Goals prevented': 'Verhinderte Tore',
    'Passes': 'Pässe', 'Pass accuracy': 'Passquote', 'Dribbles': 'Dribblings', 'Successful dribbles': 'Dribblings',
    'Interceptions': 'Balleroberungen', 'Clearances': 'Klärungen', 'Tackles': 'Zweikämpfe',
    'Long balls': 'Lange Bälle', 'Crosses': 'Flanken', 'Assists': 'Vorlagen', 'xG': 'xG', 'xA': 'xA',
    'High claims': 'Flanken abgefangen', 'Sweeper actions': 'Libero-Aktionen', 'Distribution': 'Spieleröffnung',
  };

  const TABS: Array<{ id: Tab; label: string }> = [
    { id: 'spiel', label: 'Spiel' },
    { id: 'tabelle', label: 'Tabelle' },
    { id: 'aufstellung', label: 'Aufstellung' },
  ];

  const ownTeams = $derived(new Set(match ? [match.home.id, match.away.id] : []));
</script>

{#snippet face(photo: string | null | undefined)}
  <!-- A placeholder where there is no photo keeps the names in one column. -->
  {#if photo && !noPhoto.has(photo)}
    <img class="face" src={photo} alt="" loading="lazy" onerror={() => hidePhoto(photo)} />
  {:else}
    <span class="face none"></span>
  {/if}
{/snippet}

<aside class="panel" class:focused aria-label="Spielinfos" bind:this={panelEl}>
  <header>
    <nav class="tabs">
      {#each TABS as t (t.id)}
        <button
          data-focusable
          data-focus-default={tab === t.id && !player ? true : undefined}
          class:active={tab === t.id}
          onclick={() => { tab = t.id; }}
        >{t.label}</button>
      {/each}
    </nav>
    <button class="close" data-focusable aria-label="Spielinfos schließen" onclick={onClose}>✕</button>
  </header>

  {#if !linked}
    <p class="faint pad">
      Keine Perspektive ist mit einem Spiel verknüpft. Ordne in der Übersicht unter
      „Bearbeiten“ einer Perspektive ein Spiel zu.
    </p>
  {:else if !matchId}
    <p class="faint pad">
      Das zugeordnete Spiel stammt aus „{linked.fixtureProvider}“. Spielinfos, Tabelle und
      Aufstellung gibt es nur für FotMob-Spiele — ordne das Spiel unter „Bearbeiten“ neu zu.
    </p>
  {:else if !match}
    <p class="faint pad">{error ?? 'lädt …'}</p>
  {:else}
    <div class="score">
      <span class="team">{match.home.name}</span>
      <strong class="result">{match.phase === 1 ? '–:–' : `${match.home.score}:${match.away.score}`}</strong>
      <span class="team away">{match.away.name}</span>
      <span class="clock">{clockLabel}</span>
    </div>

    {#if !running}
      <!-- nothing to warn about -->
    {:else if behindSeconds === null}
      <p class="note">Bildverzögerung unbekannt — die Infos sind live und können dem Bild voraus sein.</p>
    {:else if matchAhead || (tab === 'tabelle' && tableAhead)}
      <p class="note">Noch keine verzögerten Daten — gerade live, also evtl. dem Bild voraus.</p>
    {/if}

    <div class="body scroll">
      {#if player}
        <section class="player">
          <button data-focusable data-focus-default class="back" onclick={closePlayer}>← zurück</button>
          <div class="who">
            {#if player.photo && !noPhoto.has(player.photo)}
              <img class="portrait" src={player.photo} alt="" onerror={() => hidePhoto(player!.photo!)} />
            {/if}
            <h3>{player.name}</h3>
          </div>
          <dl>
            {#if player.team}<dt>Verein</dt><dd>{player.team}</dd>{/if}
            {#if player.position}<dt>Position</dt><dd>{player.position}</dd>{/if}
            {#if player.age !== null}<dt>Alter</dt><dd>{player.age}</dd>{/if}
            {#if player.country}<dt>Nation</dt><dd>{player.country}</dd>{/if}
            {#if player.heightCm}<dt>Größe</dt><dd>{player.heightCm} cm</dd>{/if}
            {#if player.marketValueEur}<dt>Marktwert</dt><dd>{euro(player.marketValueEur)}</dd>{/if}
          </dl>
          {#if player.season.matches !== null}
            <h4>Saison{player.season.competition ? ` · ${player.season.competition}` : ''}</h4>
            <div class="stats">
              <span><strong>{player.season.matches}</strong> Spiele</span>
              <span><strong>{player.season.goals ?? 0}</strong> Tore</span>
              <span><strong>{player.season.assists ?? 0}</strong> Vorlagen</span>
              {#if player.season.rating}<span><strong>{player.season.rating.toFixed(1)}</strong> Note</span>{/if}
            </div>
          {/if}
          {#if player.recent.length > 0}
            <h4>Letzte Spiele</h4>
            <ul class="recent">
              {#each player.recent as r, i (i)}
                <li>
                  <span class="faint">{r.date ? new Date(r.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : ''}</span>
                  <span class="opp">{r.opponent}</span>
                  <span>{r.goals > 0 ? '⚽'.repeat(Math.min(r.goals, 3)) : ''}{r.assists > 0 ? ` ${r.assists} V` : ''}</span>
                  {#if r.rating}<span class="rating" class:good={r.rating >= 7.5}>{r.rating.toFixed(1)}</span>{:else}<span></span>{/if}
                </li>
              {/each}
            </ul>
          {/if}
          {#if player.traits.length > 0}
            <h4>Stärken im Vergleich</h4>
            <ul class="traits">
              {#each player.traits as t (t.title)}
                <li>
                  <span>{TRAITS[t.title] ?? t.title}</span>
                  <span class="bar"><span style:width={`${Math.round(t.percentile * 100)}%`}></span></span>
                </li>
              {/each}
            </ul>
          {/if}
        </section>

      {:else if tab === 'spiel'}
        {#if match.events.length === 0}
          <p class="faint">Noch keine Ereignisse.</p>
        {:else}
          <ul class="events">
            {#each [...match.events].reverse() as e (e.id)}
              <li class:away={e.side === 'away'} class:goal={e.score !== null}>
                <span class="min">{e.minute !== null ? `${e.minute}'` : ''}</span>
                <span class="icon">{ICON[e.kind]}</span>
                <button class="who" data-focusable disabled={!e.playerId} onclick={() => openPlayer({ id: e.playerId })}>
                  <span>{e.player}{NOTE[e.kind] ?? ''}</span>
                  {#if e.assist}<span class="faint small">Vorlage {e.assist}</span>{/if}
                </button>
                {#if e.score}<strong class="sc">{e.score[0]}:{e.score[1]}</strong>{/if}
              </li>
            {/each}
          </ul>
        {/if}
        {#if match.venue}<p class="faint small">{match.venue}</p>{/if}

      {:else if tab === 'tabelle'}
        {#if !tables}
          <p class="faint">lädt …</p>
        {:else if tables.length === 0}
          <p class="faint">Für diesen Wettbewerb gibt es keine Tabelle.</p>
        {:else}
          {#each tables as table (table.name)}
            {#if tables.length > 1}<h4>{table.name}</h4>{/if}
            <table class="league">
              <thead>
                <tr><th>#</th><th class="name">Team</th><th>Sp</th><th>Tore</th><th>Pkt</th></tr>
              </thead>
              <tbody>
                {#each table.rows as row, i (row.teamId)}
                  <tr class:own={ownTeams.has(row.teamId)} class:live={row.live}>
                    <td>{i + 1}</td>
                    <td class="name">{row.name}</td>
                    <td>{row.played}</td>
                    <td>{row.goalsFor}:{row.goalsAgainst}</td>
                    <td><strong>{row.points}</strong></td>
                  </tr>
                {/each}
              </tbody>
            </table>
          {/each}
          <p class="faint small">Grün: Spiel läuft gerade, Stand inklusive.</p>
        {/if}

      {:else}
        {#if !match.lineups}
          <p class="faint">Aufstellungen sind noch nicht bekannt.</p>
        {:else}
          {#each [{ team: match.home, lineup: match.lineups.home }, { team: match.away, lineup: match.lineups.away }] as side (side.team.id)}
            <h4>{side.team.name}{side.lineup.formation ? ` · ${side.lineup.formation}` : ''}</h4>
            {#if match.phase === 1}<p class="faint small">voraussichtlich</p>{/if}
            <ul class="lineup">
              {#each side.lineup.starters as p, i (`${p.id}-${i}`)}
                <li>
                  <button data-focusable onclick={() => openPlayer(p)}>
                    <span class="nr">{p.number ?? ''}</span>
                    {@render face(p.photo)}
                    <span class="pname">{p.name}</span>
                    {#if p.rating}<span class="rating" class:good={p.rating >= 7.5}>{p.rating.toFixed(1)}</span>{/if}
                  </button>
                </li>
              {/each}
            </ul>
            {#if side.lineup.subs.length > 0}
              <details>
                <summary class="faint small" data-focusable>Bank ({side.lineup.subs.length})</summary>
                <ul class="lineup">
                  {#each side.lineup.subs as p, i (`${p.id}-${i}`)}
                    <li>
                      <button data-focusable onclick={() => openPlayer(p)}>
                        <span class="nr">{p.number ?? ''}</span>
                        {@render face(p.photo)}
                        <span class="pname">{p.name}</span>
                        {#if p.rating}<span class="rating">{p.rating.toFixed(1)}</span>{/if}
                      </button>
                    </li>
                  {/each}
                </ul>
              </details>
            {/if}
            {#if side.lineup.coach}<p class="faint small">Trainer: {side.lineup.coach}</p>{/if}
          {/each}
        {/if}
      {/if}

      {#if playerLoading}<p class="faint">lädt Spieler …</p>{/if}
      {#if error}<p class="error small">{error}</p>{/if}
    </div>
  {/if}

  {#if !focused}
    <p class="hint faint small">Info-Taste / i: Panel bedienen · Zurück: schließen</p>
  {/if}
</aside>

<style>
  .panel {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--gap);
    background: var(--bg-raised);
    border-left: 1px solid var(--border);
    padding: var(--safe-inset) var(--pad) var(--safe-inset) var(--pad);
    min-width: 0;
    font-size: var(--font-sm);
  }

  .panel.focused { border-left-color: var(--accent); }

  header { display: flex; align-items: center; gap: var(--gap); }
  .tabs { display: flex; gap: 4px; flex: 1 1 auto; min-width: 0; }
  .tabs button { flex: 1 1 0; padding: 0.4em 0.5em; font-size: var(--font-sm); background: transparent; }
  .tabs button.active { background: var(--accent-dim); border-color: var(--accent); }
  .close { background: transparent; border-color: transparent; padding: 0.4em 0.6em; }

  .score {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 4px var(--gap);
    font-size: var(--font-base);
  }
  .score .team { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .score .away { text-align: right; }
  .score .result { font-size: var(--font-xl); font-variant-numeric: tabular-nums; }
  .score .clock { grid-column: 1 / -1; text-align: center; color: var(--accent); font-size: var(--font-sm); }

  .note { margin: 0; color: var(--warn); font-size: var(--font-sm); line-height: 1.35; }

  .body {
    flex: 1 1 auto;
    min-height: 0;
    /* Room for focus rings inside the scroll box. */
    padding: 6px;
    margin: -6px;
  }

  h3 { margin: 0.2em 0; font-size: var(--font-lg); }
  h4 { margin: 0.8em 0 0.3em; font-size: var(--font-sm); color: var(--text-dim); }
  .pad { padding: var(--gap) 0; line-height: 1.4; }
  .small { font-size: calc(var(--font-sm) * 0.9); }
  .error { color: var(--danger); }

  .events, .lineup, .traits { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
  .events li { display: grid; grid-template-columns: 2.6em 1.6em 1fr auto; align-items: center; gap: 4px; }
  .events li.goal { font-weight: 600; }
  .events .min { color: var(--text-dim); font-variant-numeric: tabular-nums; text-align: right; }
  .events .sc { color: var(--accent); font-variant-numeric: tabular-nums; }
  .events .who {
    display: flex; flex-direction: column; align-items: flex-start; text-align: left;
    background: transparent; border-color: transparent; padding: 0.25em 0.4em; min-width: 0;
  }
  .events .who:disabled { cursor: default; opacity: 1; }
  .events li.away .who { color: var(--text-dim); }

  table.league { width: 100%; border-collapse: collapse; font-variant-numeric: tabular-nums; }
  .league th, .league td { padding: 0.25em 0.3em; text-align: right; }
  .league th { color: var(--text-faint); font-weight: 500; }
  .league .name { text-align: left; max-width: 10em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .league tr.own { background: rgba(255, 255, 255, 0.07); font-weight: 600; }
  .league tr.live td:first-child { box-shadow: inset 3px 0 0 var(--accent); }

  .lineup button {
    width: 100%; display: flex; gap: 0.6em; align-items: center; text-align: left;
    background: transparent; border-color: transparent; padding: 0.25em 0.4em;
  }
  .lineup .nr { width: 1.8em; color: var(--text-faint); text-align: right; font-variant-numeric: tabular-nums; }
  .lineup .face {
    width: 1.9em; height: 1.9em; flex: 0 0 auto;
    border-radius: 50%; object-fit: cover; object-position: top;
    background: var(--border);
  }
  .lineup .face.none { opacity: 0.5; }
  .who { display: flex; align-items: center; gap: var(--gap); }
  .portrait { width: 4.5em; height: 4.5em; border-radius: 50%; object-fit: cover; object-position: top; background: var(--border); }
  .lineup .pname { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .rating { padding: 0 0.4em; border-radius: 4px; background: var(--border-strong); font-variant-numeric: tabular-nums; }
  .rating.good { background: var(--accent-dim); }
  details summary { cursor: pointer; margin: 4px 0; }

  .player dl { display: grid; grid-template-columns: auto 1fr; gap: 4px var(--gap); margin: 0.5em 0; }
  .player dt { color: var(--text-dim); }
  .player dd { margin: 0; }
  .stats { display: flex; flex-wrap: wrap; gap: var(--gap); }
  .stats strong { font-size: var(--font-base); }
  .traits li { display: grid; grid-template-columns: 1fr 40%; gap: var(--gap); align-items: center; }
  .bar { height: 6px; background: var(--border); border-radius: 3px; overflow: hidden; }
  .bar span { display: block; height: 100%; background: var(--accent); }
  .back { background: transparent; padding: 0.3em 0.6em; }
  .recent { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
  .recent li { display: grid; grid-template-columns: 3.2em 1fr auto auto; gap: var(--gap); align-items: center; }
  .recent .opp { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .hint { margin: 0; }
</style>
